import { useEffect, useState } from "react";
import { Check, ClipboardCheck, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Field, Empty } from "@/components/common";

const fmtDT = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-");
const COND_STYLE = { Good: "text-emerald-600", Damaged: "text-rose-600", Present: "text-emerald-600", Missing: "text-rose-600", Complete: "text-emerald-600", Incomplete: "text-amber-600" };

function ItemDialog({ open, onClose, initial, vehicleId, onSaved }) {
  const [f, setF] = useState({ name: "", quantity: 1, condition: "", status: "", expiry: "", notes: "" });
  useEffect(() => { if (open) setF(initial ? { name: initial.name, quantity: initial.quantity, condition: initial.condition || "", status: initial.status || "", expiry: initial.expiry || "", notes: initial.notes || "" } : { name: "", quantity: 1, condition: "Good", status: "", expiry: "", notes: "" }); }, [open, initial]);
  const kind = initial?.kind || "qty_condition";
  const save = async () => {
    if (!f.name.trim()) return toast.error("Item name is required");
    try {
      const body = { ...f, quantity: Number(f.quantity) };
      if (initial) await api.put(`/inventory/${initial.id}`, body);
      else await api.post(`/vehicles/${vehicleId}/inventory`, body);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card" data-testid="inventory-item-dialog">
        <DialogHeader><DialogTitle>{initial ? `Update — ${initial.name}` : "Add Custom Equipment"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Item Name *" className="col-span-2"><Input data-testid="item-name-input" value={f.name} onChange={set("name")} disabled={!!initial && !initial.is_custom} placeholder="e.g. GPS Device, Fuel Lock, Container Seals" /></Field>
          {(kind.includes("qty")) && <Field label="Quantity"><Input data-testid="item-qty-input" type="number" min="0" value={f.quantity} onChange={set("quantity")} /></Field>}
          {(kind.includes("condition")) && (
            <Field label="Condition">
              <select data-testid="item-condition-select" className="native-select" value={f.condition} onChange={set("condition")}>
                {["Good", "Damaged"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          )}
          {kind === "status" && (
            <Field label="Status">
              <select data-testid="item-status-select" className="native-select" value={f.status} onChange={set("status")}>
                {["Present", "Missing"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          )}
          {kind === "status_complete" && (
            <Field label="Status">
              <select data-testid="item-status-select" className="native-select" value={f.status} onChange={set("status")}>
                {["Complete", "Incomplete"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          )}
          {kind === "expiry_status" && (
            <>
              <Field label="Expiry Date"><Input data-testid="item-expiry-input" type="date" value={f.expiry} onChange={set("expiry")} /></Field>
              <Field label="Status">
                <select data-testid="item-status-select" className="native-select" value={f.status} onChange={set("status")}>
                  {["Present", "Missing"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </>
          )}
          <Field label="Notes" className="col-span-2"><Input data-testid="item-notes-input" value={f.notes} onChange={set("notes")} /></Field>
        </div>
        <DialogFooter><Button data-testid="item-save-btn" onClick={save}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HandoverDialog({ open, onClose, vehicle, items, onSaved }) {
  const [type, setType] = useState("handover");
  const [driverId, setDriverId] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [checks, setChecks] = useState({});
  useEffect(() => {
    if (!open) return;
    setType("handover");
    setChecks(Object.fromEntries(items.map((i) => [i.id, { ok: true, note: "" }])));
    api.get("/drivers").then((r) => {
      setDrivers(r.data);
      const cur = r.data.find((d) => d.current_vehicle_id === vehicle.id && d.employment_status === "Active");
      setDriverId(cur?.id || "");
    });
  }, [open, items, vehicle]);
  const submit = async () => {
    try {
      const { data } = await api.post(`/vehicles/${vehicle.id}/handover`, {
        type, driver_id: driverId || null,
        checks: items.map((i) => ({ item_id: i.id, ok: checks[i.id]?.ok ?? true, note: checks[i.id]?.note || "" })),
      });
      toast.success(data.issues.length ? `Recorded with ${data.issues.length} issue(s)` : "Handover recorded — all items OK");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto" data-testid="handover-dialog">
        <DialogHeader><DialogTitle>Equipment {type === "handover" ? "Handover" : "Return"} Check — {vehicle.vehicle_number}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Check Type">
            <select data-testid="handover-type-select" className="native-select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="handover">Handover (assign to driver)</option>
              <option value="return">Return (driver leaves vehicle)</option>
            </select>
          </Field>
          <Field label="Driver *">
            <select data-testid="handover-driver-select" className="native-select" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Select driver…</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-2 space-y-2">
          {items.map((i) => {
            const c = checks[i.id] || { ok: true, note: "" };
            return (
              <div key={i.id} data-testid={`handover-item-${i.id}`} className={`flex flex-wrap items-center gap-2 rounded-lg border p-2.5 text-sm ${!c.ok ? "border-rose-300 bg-rose-50/60 dark:bg-rose-950/20" : ""}`}>
                <div className="min-w-0 flex-1 font-medium">{i.name} <span className="text-xs text-muted-foreground">× {i.quantity}</span></div>
                <div className="flex gap-1">
                  <button type="button" data-testid={`handover-ok-${i.id}`} onClick={() => setChecks({ ...checks, [i.id]: { ...c, ok: true } })}
                    className={`flex h-8 w-8 items-center justify-center rounded-md border ${c.ok ? "border-emerald-500 bg-emerald-500 text-white" : "text-muted-foreground"}`}><Check className="h-4 w-4" /></button>
                  <button type="button" data-testid={`handover-issue-${i.id}`} onClick={() => setChecks({ ...checks, [i.id]: { ...c, ok: false } })}
                    className={`flex h-8 w-8 items-center justify-center rounded-md border ${!c.ok ? "border-rose-500 bg-rose-500 text-white" : "text-muted-foreground"}`}><X className="h-4 w-4" /></button>
                </div>
                {!c.ok && <Input data-testid={`handover-note-${i.id}`} className="w-full" placeholder="Issue note (missing / damaged…)" value={c.note} onChange={(e) => setChecks({ ...checks, [i.id]: { ...c, note: e.target.value } })} />}
              </div>
            );
          })}
        </div>
        <DialogFooter><Button data-testid="handover-submit-btn" onClick={submit}>Record {type === "handover" ? "Handover" : "Return"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InventoryTab({ vehicle, canEdit }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [itemOpen, setItemOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const load = () => api.get(`/vehicles/${vehicle.id}/inventory`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [vehicle.id]); // eslint-disable-line
  if (!data) return <p className="py-6 text-sm text-muted-foreground">Loading inventory…</p>;
  const { items, handovers } = data;
  const del = async (i) => {
    if (!window.confirm(`Delete ${i.name}?`)) return;
    try { await api.delete(`/inventory/${i.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4" data-testid="inventory-tab">
      <div className="mb-3 flex flex-wrap gap-2">
        {canEdit && <Button variant="outline" size="sm" data-testid="add-custom-item-btn" onClick={() => { setEdit(null); setItemOpen(true); }}><Plus className="mr-1 h-4 w-4" />Custom Item</Button>}
        <Button size="sm" data-testid="handover-btn" onClick={() => setHandoverOpen(true)} className="bg-primary"><ClipboardCheck className="mr-1 h-4 w-4" />Handover / Return Check</Button>
      </div>
      <div className="divide-y rounded-lg border">
        {items.map((i) => (
          <div key={i.id} data-testid={`inventory-item-${i.id}`} className="flex items-center gap-3 px-3 py-2.5 text-sm">
            <div className="min-w-0 flex-1">
              <span className="font-medium">{i.name}</span>
              {i.is_custom && <span className="ml-1.5 rounded bg-accent px-1 py-0.5 text-[10px] font-semibold text-accent-foreground">CUSTOM</span>}
              <div className="text-xs text-muted-foreground">
                Qty {i.quantity}
                {i.condition && <> · <span className={COND_STYLE[i.condition]}>{i.condition}</span></>}
                {i.status && <> · <span className={COND_STYLE[i.status]}>{i.status}</span></>}
                {i.expiry && <> · Exp {fmtDate(i.expiry)}</>}
                {i.notes && <> · {i.notes}</>}
              </div>
            </div>
            {canEdit && (
              <>
                <button data-testid={`inventory-edit-${i.id}`} onClick={() => { setEdit(i); setItemOpen(true); }} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                {i.is_custom && <button data-testid={`inventory-delete-${i.id}`} onClick={() => del(i)} className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <Empty text="No items" />}
      </div>
      <h4 className="label-xs mb-2 mt-5">Handover Records</h4>
      <div className="space-y-2" data-testid="handover-history">
        {handovers.map((h) => (
          <div key={h.id} data-testid={`handover-row-${h.id}`} className={`rounded-lg border p-3 text-sm ${h.issues.length ? "border-rose-200 bg-rose-50/50 dark:border-rose-900 dark:bg-rose-950/20" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><b>{h.driver_name}</b> · {h.type === "handover" ? "Handover" : "Return"} · {fmtDT(h.checked_at)}</div>
              {h.issues.length ? <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid={`handover-issues-${h.id}`}>{h.issues.length} issue(s)</span> : <span className="text-xs font-semibold text-emerald-600">All OK</span>}
            </div>
            {h.issues.length > 0 && <div className="mt-1 text-xs text-rose-700 dark:text-rose-300">{h.issues.join(" · ")}</div>}
          </div>
        ))}
        {handovers.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No handover checks recorded yet.</p>}
      </div>
      <ItemDialog open={itemOpen} onClose={() => setItemOpen(false)} initial={edit} vehicleId={vehicle.id} onSaved={load} />
      <HandoverDialog open={handoverOpen} onClose={() => setHandoverOpen(false)} vehicle={vehicle} items={items} onSaved={load} />
    </div>
  );
}
